"use client"

import { Button, InitialsAvatar, cn } from '@yeshuman/sdk'
import type { JobProspectMatch } from '../../hooks/use-job-prospect-matches'
import type { OpenJobWorkspacePersonHandler } from '../../lib/job-workspace-person-paths'

export type SmartAnalysisBeyondPipelineProps = {
  matches: JobProspectMatch[]
  onReviewInvite: () => void
  onOpenPerson?: OpenJobWorkspacePersonHandler
  readOnly?: boolean
  className?: string
}

export function SmartAnalysisBeyondPipeline({
  matches,
  onReviewInvite,
  onOpenPerson,
  readOnly = false,
  className,
}: SmartAnalysisBeyondPipelineProps) {
  const strongMatches = matches.filter((match) => match.match_percent >= 80)

  if (strongMatches.length === 0) return null

  const avatars = strongMatches.slice(0, 3)

  return (
    <section
      aria-label="Beyond your pipeline"
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-dashed border-border/80 bg-muted/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex -space-x-2">
          {avatars.map((match) => {
            const analysis = (match as { analysis?: unknown }).analysis
            const open =
              readOnly && onOpenPerson && analysis && typeof analysis === 'object'
                ? () => onOpenPerson(match.profile_id, match.name, match.evaluation_id)
                : undefined
            const avatar = (
              <InitialsAvatar
                src={match.avatar_url}
                colorSeed={match.profile_id}
                alt={match.name}
                className="size-8 border-2 border-background"
              />
            )
            if (!open) return <span key={match.profile_id}>{avatar}</span>
            return (
              <button
                key={match.profile_id}
                type="button"
                className="rounded-full focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                aria-label={`Open match analysis for ${match.name}`}
                onClick={open}
              >
                {avatar}
              </button>
            )
          })}
        </div>
        <p className="text-sm text-foreground">
          {strongMatches.length} high-estimate match
          {strongMatches.length === 1 ? '' : 'es'} are not in your pipeline.
        </p>
      </div>
      {readOnly ? null : (
        <Button type="button" variant="outline" size="sm" onClick={onReviewInvite}>
          Review in Invite
        </Button>
      )}
    </section>
  )
}
